import type { FC } from "react";
import { BriefcaseIcon, MapPinIcon, NewspaperIcon, SparklesIcon } from "@heroicons/react/24/outline";
import { cn } from "@/lib/cn";
import { FavoriteButton } from "./favorite-button";
import { IconText } from "./icon-text";
import type { CardLayoutProps } from "./layout-props";
import { PractitionerActions } from "./practitioner-actions";
import { PractitionerAvatar } from "./practitioner-avatar";
import { PractitionerTags } from "./practitioner-tags";
import { ProfessionList } from "./profession-list";
import { SkeletonBar } from "./skeleton-bar";
import { SpecialtyPill } from "./specialty-pill";
import { VerifiedName } from "./verified-name";

type CompactLayoutProps = CardLayoutProps & { showFavorite?: boolean; className?: string };

export const CompactLayoutSkeleton: FC<{ expanded?: boolean; className?: string }> = ({ expanded, className }) => (
  <div className={cn("flex w-full flex-col gap-12 p-16", className)}>
    <div className="flex items-start gap-12">
      <div className="skeleton-bar-dark size-75 shrink-0 rounded-medium" />
      <div className="flex flex-1 flex-col justify-center gap-8 self-stretch">
        <SkeletonBar tone="dark" className="h-23 w-4/5" />
        <SkeletonBar className="w-3/5" />
        <SkeletonBar className="w-2/5" />
      </div>
    </div>
    <div className="flex gap-8">
      <SkeletonBar className="h-28 w-1/4" />
      <SkeletonBar className="h-28 w-1/4" />
      <SkeletonBar className="h-28 w-1/3" />
    </div>
    <SkeletonBar className="h-28 w-full" />
    {expanded && (
      <>
        <SkeletonBar className="h-40 w-full" />
        <div className="flex gap-8">
          <SkeletonBar tone="dark" className="h-48 flex-1" />
          <SkeletonBar className="h-48 flex-1" />
        </div>
      </>
    )}
  </div>
);

export const CompactLayout: FC<CompactLayoutProps> = ({
  content,
  favorited,
  onContact,
  onFavoriteToggle,
  loading,
  expanded,
  showFavorite,
  className,
}) => {
  if (!content || loading) return <CompactLayoutSkeleton expanded={expanded} className={className} />;

  const { name, verified, professions, location, years, photoUrl, profileHref, aboutExcerpt } = content;
  const { specialties, languages, formats } = content;

  return (
    <div className={cn("flex w-full flex-col gap-12 p-16", className)}>
      <div className="flex items-start gap-12">
        <PractitionerAvatar name={name} photoUrl={photoUrl} className="size-75 shrink-0" />
        <div className="flex min-w-0 flex-1 flex-col justify-center gap-8 self-stretch">
          <VerifiedName name={name} verified={verified} className="min-w-0" nameClassName="min-w-0 truncate text-18" />
          {professions.length > 0 && (
            <IconText icon={<BriefcaseIcon className="size-18" />} className="items-start">
              <ProfessionList entries={professions} />
            </IconText>
          )}
          {location && <IconText icon={<MapPinIcon className="size-18" />}>{location}</IconText>}
          {expanded && years !== undefined && years > 0 && (
            <IconText icon={<NewspaperIcon className="size-18" />}>{years}+ Year of Experience</IconText>
          )}
        </div>
        {showFavorite && (
          <FavoriteButton
            active={favorited}
            onClick={(event) => {
              event.stopPropagation();
              onFavoriteToggle?.();
            }}
            className="-mr-8 -mt-8 shrink-0"
            iconClassName="size-24"
          />
        )}
      </div>

      <PractitionerTags languages={languages} formats={formats} size="s" hideIcons scrollable />
      <SpecialtyPill
        specialties={specialties}
        hideLabel
        pill
        scrollable
        icon={<SparklesIcon className="size-24 shrink-0 text-brand-dark-blue" />}
        className="gap-8 px-8"
      />
      {expanded && aboutExcerpt && (
        <p className="line-clamp-2 text-center text-14 text-neutral-black/70">{aboutExcerpt}</p>
      )}
      {expanded && <PractitionerActions profileHref={profileHref} onContact={onContact} className="w-full" />}
    </div>
  );
};
